import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, CheckCircle, Building, FileText, MessageCircle, ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const PreRegister = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [foundingOffer, setFoundingOffer] = useState({ buyer: {}, supplier: {} });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    organization: '',
    user_type: 'Buyer',
    city: '',
    district: '',
    state: '',
    category: '',
    message: ''
  });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const WHATSAPP_NUMBER = '918369211947';

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/founding-offer`).then(r => setFoundingOffer(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        organization: formData.organization,
        message: `Pre-Registration | User Type: ${formData.user_type} | City: ${formData.city} | District: ${formData.district} | State: ${formData.state} | Category: ${formData.category}\n\n${formData.message}`,
        source: `pre_registration_${formData.user_type.toLowerCase()}`
      });
      setSubmitted(true);
      toast.success('Pre-registration submitted! We will contact you at launch.');
    } catch (err) {
      toast.error('Failed to submit. Please try again or WhatsApp us.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring focus:outline-none";
  const buyerSlots = foundingOffer.buyer?.max_slots ? foundingOffer.buyer.max_slots - (foundingOffer.buyer.used_slots || 0) : 100;
  const supplierSlots = foundingOffer.supplier?.max_slots ? foundingOffer.supplier.max_slots - (foundingOffer.supplier.used_slots || 0) : 100;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
        <div className="max-w-md text-center bg-card border border-border rounded-lg p-10 shadow-lg">
          <CheckCircle className="h-20 w-20 mx-auto mb-6 text-gold" />
          <h1 className="text-3xl font-bold mb-3 text-navy">Thank You!</h1>
          <p className="text-muted-foreground mb-6">
            Your pre-registration interest has been recorded. We will contact you when ProcureOne Ultra opens membership today.
          </p>
          <div className="space-y-3">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I just pre-registered for ProcureOne Ultra')}`}
              target="_blank" rel="noopener noreferrer"
              className="block w-full px-6 py-3 rounded-md bg-orange text-white font-semibold"
              data-testid="prereg-whatsapp-btn"
            >
              <MessageCircle className="inline h-4 w-4 mr-2" /> Connect on WhatsApp
            </a>
            <button onClick={() => navigate('/')} className="block w-full px-6 py-3 rounded-md border border-input">
              Back to App Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button onClick={() => navigate('/')} className="text-white/70 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                <Sparkles className="h-7 w-7 text-gold" />
                <div>
                  <div className="text-lg font-bold">ProcureOne Ultra</div>
                  <div className="text-xs text-white/60">by Plus Ultra Innovation</div>
                </div>
              </div>
            </div>
            <a href="https://www.plusultrainnovation.com" className="text-sm text-white/80 hover:text-gold hidden md:inline">
              Visit Main Website →
            </a>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-5xl">
        {/* Intro */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 mb-4 px-4 py-2 rounded-full bg-orange text-white text-xs font-bold tracking-wider uppercase">
            <Calendar className="h-4 w-4" />
            <span>Founding Member Registrations Open</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy mb-3">
            Register Your <span className="text-gold">Founding Interest</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join the founding access list. First 100 Buyer organizations and first 100 Suppliers get <strong>2 Years FREE Subscription</strong>.
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto mt-2">
            <span className="font-semibold text-navy">50% off in Year 1</span> + <span className="font-semibold text-navy">100% off in Year 2</span> on yearly subscription
          </p>
        </div>

        {/* Slot stats */}
        <div className="grid md:grid-cols-2 gap-4 mb-10 max-w-3xl mx-auto">
          <div className="p-5 rounded-lg border-2 border-gold/40 bg-gold/5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold tracking-wider uppercase text-navy">Founding Buyers</div>
              <Building className="h-5 w-5 text-navy" />
            </div>
            <div className="text-3xl font-bold text-navy">{buyerSlots} <span className="text-base font-normal text-muted-foreground">of 100 left</span></div>
            <div className="text-xs text-muted-foreground mt-1">2 Years FREE • 50% Y1 + 100% Y2</div>
          </div>
          <div className="p-5 rounded-lg border-2 border-orange/40 bg-orange/5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold tracking-wider uppercase text-navy">Founding Suppliers</div>
              <FileText className="h-5 w-5 text-orange" />
            </div>
            <div className="text-3xl font-bold text-navy">{supplierSlots} <span className="text-base font-normal text-muted-foreground">of 100 left</span></div>
            <div className="text-xs text-muted-foreground mt-1">2 Years FREE • 50% Y1 + 100% Y2</div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-card border border-border rounded-lg p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-navy mb-6">Pre-Registration Form</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputClass} data-testid="prereg-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mobile Number *</label>
                <input type="tel" required value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} className={inputClass} data-testid="prereg-mobile-input" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputClass} data-testid="prereg-email-input" />
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                  📧 This email will be used for future communication and sharing of <strong>invoices &amp; GST receipts</strong>. Please ensure it is correct.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Organization / Company Name *</label>
                <input type="text" required value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} className={inputClass} data-testid="prereg-org-input" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">User Type *</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setFormData({ ...formData, user_type: 'Buyer' })} className={`p-3 rounded-md border-2 transition-colors ${formData.user_type === 'Buyer' ? 'border-navy bg-navy/5 text-navy font-semibold' : 'border-border'}`} data-testid="prereg-buyer-btn">
                  Buyer Organization
                </button>
                <button type="button" onClick={() => setFormData({ ...formData, user_type: 'Supplier' })} className={`p-3 rounded-md border-2 transition-colors ${formData.user_type === 'Supplier' ? 'border-orange bg-orange/5 text-orange font-semibold' : 'border-border'}`} data-testid="prereg-supplier-btn">
                  Supplier / Contractor
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input type="text" required value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className={inputClass} data-testid="prereg-city-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">District *</label>
                <input type="text" required value={formData.district} onChange={(e) => setFormData({ ...formData, district: e.target.value })} className={inputClass} data-testid="prereg-district-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">State *</label>
                <input type="text" required value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className={inputClass} data-testid="prereg-state-input" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category / Requirement</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className={inputClass} data-testid="prereg-category-select">
                <option value="">Select Category</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                <option value="Other">Other (specify in message)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Message</label>
              <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={4} placeholder="Tell us about your procurement needs or services..." className={inputClass} data-testid="prereg-message-input" />
            </div>

            <button type="submit" disabled={loading} className="w-full px-6 py-3 rounded-md bg-navy text-white font-semibold hover:opacity-90 disabled:opacity-50" data-testid="prereg-submit-btn">
              {loading ? 'Submitting...' : 'Submit Pre-Registration'}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              By submitting, you agree to be contacted by Plus Ultra Innovation when ProcureOne Ultra launches.
            </p>
          </form>
        </div>

        {/* Footer info */}
        <div className="mt-10 text-center text-sm text-muted-foreground">
          <p>For queries: info@plusultrainnovation.com • WhatsApp +91 8369211947</p>
        </div>
      </div>
    </div>
  );
};

export default PreRegister;
